import { Controller } from '@hotwired/stimulus'
import { format as formatTz } from 'date-fns-tz'
import parseISO from 'date-fns/parseISO'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import escapeHtml from '../../utils/sanitize'
import {
  trans,
  STATUS_BANNED,
  DUPLICATE_WARNING_TITLE,
  DUPLICATE_PRIOR_INCIDENTS,
  DUPLICATE_ACTION_USE,
  DUPLICATE_ACTION_VIEW,
  DUPLICATE_ACTION_PROCEED,
  DUPLICATE_OUTCOME_SAME_DATE,
  DUPLICATE_OUTCOME_CANNOT_RULE_OUT,
  DATE_OF_BIRTH_SHORT,
  DATE_OF_BIRTH_UNKNOWN_SHORT
} from '../../translator'

declare const accountDateFormat: string

const DEBOUNCE_MS = 300
const ENDPOINT = '/internal/api/associated-person/duplicate-check'
const BASE_PATH = '/associated-persons'

interface DuplicateMatchIndicators {
  is_banned: boolean
  prior_incidents_count: number
  has_photo: boolean
}

interface DuplicateMatch {
  id: string
  full_name: string
  date_of_birth: string | null
  date_of_birth_display: string | null
  duplicate_screening_outcome: 'same_date' | 'possible_match' | 'possible_match_swapped'
  indicators: DuplicateMatchIndicators
}

interface DuplicateCheckResult {
  has_matches: boolean
  matches: DuplicateMatch[]
}

interface DateOfBirthParts {
  year_from: number | null
  year_to: number | null
  month: number | null
  day: number | null
}

/**
 * Screens the create form for likely duplicate Associated Persons.
 *
 * Check whenever the full name and any part of a date of birth are present.
 * Matches render a warning banner above the form as a horizontally scrollable
 * list of cards; each card offers its own resolution actions: opening that
 * record to edit, or viewing it in a new tab. A single proceed action below
 * the list dismisses the banner so the new record can be saved.
 * If every watched field is cleared the banner is dismissed immediately with no request.
 * Network failures are swallowed silently so the form always remains usable.
 */
export default class DuplicateCheckController extends Controller {
  static targets = ['fullName', 'day', 'month', 'yearFrom', 'yearTo', 'banner']

  declare readonly fullNameTarget: HTMLInputElement
  declare readonly dayTarget: HTMLInputElement
  declare readonly monthTarget: HTMLInputElement
  declare readonly yearFromTarget: HTMLInputElement
  declare readonly yearToTarget: HTMLInputElement
  declare readonly bannerTarget: HTMLElement
  declare readonly hasBannerTarget: boolean

  private debounceTimer: ReturnType<typeof setTimeout> | null = null

  connect (): void {
    this.scheduleCheck = this.scheduleCheck.bind(this)
    this.onActionClick = this.onActionClick.bind(this)

    if (this.hasBannerTarget) {
      this.bannerTarget.addEventListener('click', this.onActionClick)
    }
  }

  disconnect (): void {
    this.clearTimer()

    if (this.hasBannerTarget) {
      this.bannerTarget.removeEventListener('click', this.onActionClick)
    }
  }

  scheduleCheck (): void {
    this.clearTimer()

    if (this.fullName() === '' || !this.hasAnyDateOfBirthComponent()) {
      this.hideBanner()

      return
    }

    this.debounceTimer = setTimeout(() => {
      this.runCheck().catch(() => undefined)
    }, DEBOUNCE_MS)
  }

  private async runCheck (): Promise<void> {
    const fullName = this.fullName()

    if (fullName === '' || !this.hasAnyDateOfBirthComponent()) {
      this.hideBanner()

      return
    }

    try {
      const response = await fetchWithHandlingLoginRedirect(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_name: fullName, date_of_birth_parts: this.dateOfBirthParts() })
      })

      if (!response.ok) {
        return
      }

      const data = await response.json() as { result?: DuplicateCheckResult }
      const result = data.result

      if (result?.has_matches === true && Array.isArray(result.matches) && result.matches.length > 0) {
        this.renderBanner(result.matches)
      } else {
        this.hideBanner()
      }
    } catch {
      // Duplicate screening is advisory: a failed check must never block the form.
    }
  }

  private fullName (): string {
    return this.fullNameTarget.value.trim()
  }

  private dateOfBirthParts (): DateOfBirthParts {
    return {
      year_from: this.numberOrNull(this.yearFromTarget.value),
      year_to: this.numberOrNull(this.yearToTarget.value),
      month: this.numberOrNull(this.monthTarget.value),
      day: this.numberOrNull(this.dayTarget.value)
    }
  }

  private hasAnyDateOfBirthComponent (): boolean {
    const parts = this.dateOfBirthParts()

    return parts.year_from !== null || parts.year_to !== null || parts.month !== null || parts.day !== null
  }

  private numberOrNull (rawValue: string): number | null {
    const trimmed = rawValue.trim()

    if (trimmed === '') {
      return null
    }

    const parsed = Number(trimmed)

    return Number.isInteger(parsed) ? parsed : null
  }

  private renderBanner (matches: DuplicateMatch[]): void {
    if (!this.hasBannerTarget) {
      return
    }

    this.bannerTarget.classList.remove('d-none')
    this.bannerTarget.innerHTML = `
      <div class="alert alert-secondary" role="alert">
        <h5 class="mb-2">${trans(DUPLICATE_WARNING_TITLE, {}, 'associated-persons')}</h5>
        <div class="d-flex duplicate-check__matches" style="gap:0.5rem;overflow-x:auto;">
          ${matches.map(match => this.renderMatch(match)).join('')}
        </div>
        <button type="button" class="btn btn-sm btn-raven-light mt-3" data-duplicate-action="proceed">${trans(DUPLICATE_ACTION_PROCEED, {}, 'associated-persons')}</button>
      </div>
    `
  }

  private onActionClick (event: Event): void {
    const trigger = (event.target as HTMLElement).closest('[data-duplicate-action]') as HTMLElement | null

    if (trigger === null) {
      return
    }

    if (trigger.dataset.duplicateAction === 'proceed') {
      this.proceed()

      return
    }

    const id = trigger.closest('[data-duplicate-id]')?.getAttribute('data-duplicate-id')

    if (id === null || id === undefined) {
      return
    }

    if (trigger.dataset.duplicateAction === 'use-existing') {
      this.redirect(`${BASE_PATH}/${id}/edit`)
    } else if (trigger.dataset.duplicateAction === 'view-existing') {
      this.openInNewTab(`${BASE_PATH}/${id}`)
    }
  }

  private proceed (): void {
    this.hideBanner()
    this.bannerTarget.dispatchEvent(new CustomEvent('duplicate-check:proceed', { bubbles: true }))
  }

  redirect (url: string): void {
    window.location.href = url
  }

  openInNewTab (url: string): void {
    window.open(url, '_blank', 'noopener')
  }

  private renderMatch (match: DuplicateMatch): string {
    const meta: string[] = []

    meta.push(this.renderDob(match.date_of_birth, match.date_of_birth_display))
    meta.push(trans(DUPLICATE_PRIOR_INCIDENTS, { '%count%': match.indicators.prior_incidents_count }, 'associated-persons'))

    const outcomeChip = match.duplicate_screening_outcome === 'same_date'
      ? `<span class="associated-person-chip associated-person-chip--danger text-uppercase ml-2">${trans(DUPLICATE_OUTCOME_SAME_DATE, {}, 'associated-persons')}</span>`
      : `<span class="associated-person-chip associated-person-chip--warning text-uppercase ml-2">${trans(DUPLICATE_OUTCOME_CANNOT_RULE_OUT, {}, 'associated-persons')}</span>`

    const bannedChip = match.indicators.is_banned
      ? `<span class="associated-person-chip associated-person-chip--danger ml-2">${trans(STATUS_BANNED, {}, 'associated-persons')}</span>`
      : ''

    return `
      <div class="duplicate-check__match border rounded p-2 d-flex justify-content-between align-items-center" data-duplicate-id="${escapeHtml(match.id)}" style="flex-shrink:0;min-width:20rem;gap:0.5rem;">
        <div style="min-width:0;">
          <strong>${escapeHtml(match.full_name)}</strong>${outcomeChip}${bannedChip}
          <div class="text-muted small">${meta.join(' · ')}</div>
        </div>
        <div class="d-flex flex-column" style="gap:0.25rem;flex-shrink:0;">
          <button type="button" class="btn btn-sm btn-raven-light mr-0" data-duplicate-action="view-existing">${trans(DUPLICATE_ACTION_VIEW, {}, 'associated-persons')}<i class="fas fa-up-right-from-square ml-1"></i></button>
          <button type="button" class="btn btn-sm btn-raven-light mr-0" data-duplicate-action="use-existing">${trans(DUPLICATE_ACTION_USE, {}, 'associated-persons')}</button>
        </div>
      </div>
    `
  }

  private renderDob (dob: string | null, dobDisplay: string | null): string {
    if (dob !== null) {
      const dateFormat = accountDateFormat.replace('mm', 'MM')

      return `${trans(DATE_OF_BIRTH_SHORT, {}, 'associated-persons')} ${formatTz(parseISO(dob), dateFormat)}`
    }
    if (dobDisplay !== null) {
      return `${trans(DATE_OF_BIRTH_SHORT, {}, 'associated-persons')} ${escapeHtml(dobDisplay)}`
    }

    return trans(DATE_OF_BIRTH_UNKNOWN_SHORT, {}, 'associated-persons')
  }

  private hideBanner (): void {
    if (!this.hasBannerTarget) {
      return
    }

    this.bannerTarget.classList.add('d-none')
    this.bannerTarget.innerHTML = ''
  }

  private clearTimer (): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }
}
